import { fmtBRL } from '@/lib/utils';
import type { Lancamento } from '@/mobile/modulos/lancamentos/types';

interface FauxDocProps {
  item: Pick<Lancamento, 'forn' | 'cnpj' | 'cat' | 'date' | 'val'>;
}

/**
 * Fac-símile estilizado de Nota Fiscal (DANFE) — exibido quando não há
 * imagem real da câmera no lançamento.
 *
 * Paleta DANFE: azul #1d4099, cinza escuro #26303f.
 * Sem tokens globais intencionalmente — este componente simula papel branco
 * independente do tema (light/dark) do app.
 */
export function FauxDoc({ item }: FauxDocProps) {
  const nrNota = String(Math.abs(item.forn.charCodeAt(0) * 821 + (item.val % 999))).padStart(
    6,
    '0',
  );

  const rows: [string, string][] = [
    ['Descrição', 'Material de consumo'],
    ['Categoria', item.cat],
    ['Qtde', '1'],
  ];

  return (
    <div
      aria-label="Fac-símile de nota fiscal"
      style={{
        background: '#fff',
        color: '#26303f',
        padding: '16px 16px 18px',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      {/* Cabeçalho DANFE */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          borderBottom: '2px solid #1d4099',
          paddingBottom: 8,
          marginBottom: 10,
        }}
      >
        <div>
          <div
            style={{
              fontWeight: 800,
              color: '#1d4099',
              fontSize: 12,
              letterSpacing: '-0.01em',
            }}
          >
            NOTA FISCAL ELETRÔNICA
          </div>
          <div
            style={{
              fontSize: 9,
              color: '#6b7686',
              fontFamily: 'monospace',
              marginTop: 2,
            }}
          >
            DANFE · Série 1
          </div>
        </div>
        <div
          style={{
            textAlign: 'right',
            fontFamily: 'monospace',
            fontSize: 9,
            color: '#26303f',
          }}
        >
          Nº {nrNota}
          <br />
          {item.date}/2026
        </div>
      </div>

      {/* Emitente */}
      <div style={{ fontSize: 10.5, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 700 }}>{item.forn}</div>
        <div style={{ color: '#6b7686', fontFamily: 'monospace' }}>
          CNPJ {item.cnpj || '10.482.336/0001-55'}
        </div>
      </div>

      {/* Itens */}
      <div style={{ marginTop: 10, borderTop: '1px solid #e7ecf2', paddingTop: 8 }}>
        {rows.map(([k, v], i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 9.5,
              padding: '3px 0',
              color: '#46505f',
            }}
          >
            <span>{k}</span>
            <span style={{ fontFamily: 'monospace' }}>{v}</span>
          </div>
        ))}
      </div>

      {/* Total */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginTop: 10,
          borderTop: '2px solid #1d4099',
          paddingTop: 8,
        }}
      >
        <span style={{ fontSize: 10, fontWeight: 700, color: '#1d4099' }}>TOTAL</span>
        <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13 }}>
          {fmtBRL(item.val)}
        </span>
      </div>
    </div>
  );
}
